/**
 * Application Configuration
 * Centralized configuration for environment-specific settings
 * 
 * Vite Environment Variables:
 * - Must be prefixed with VITE_ to be exposed to the client
 * - Access via import.meta.env.VITE_*
 * - Define in .env.local or .env files
 */

// Validate required environment variables
const getRequiredEnv = (key: string): string => {
  const value = import.meta.env[key]
  if (!value) {
    throw new Error(
      `Missing required environment variable: ${key}\n` +
      `Please set ${key} in your .env.local file or environment configuration.`
    )
  }
  return value
}

export const config = {
  /**
   * API Base URL - configured per environment
   * Set VITE_API_URL in .env.local
   * Required: Will throw error if not set
   */
  apiBaseUrl: getRequiredEnv('VITE_API_URL'),
  
  /**
   * Hotjar Site ID - for user behavior analytics
   * Set VITE_HOTJAR_ID in .env.local or Azure environment variables
   * Optional: Hotjar will only load if this is set
   */
  hotjarId: import.meta.env.VITE_HOTJAR_ID,
  
  /**
   * Canny Feedback configuration
   * Optional: widget will only load if app ID is set
   */
  cannyAppId: import.meta.env.VITE_CANNY_APP_ID,
  cannyBoardId: import.meta.env.VITE_CANNY_BOARD_ID,
  // Public URL of your Canny feedback/bug board (e.g. https://your-company.canny.io/bugs)
  cannyFeedbackUrl: import.meta.env.VITE_CANNY_FEEDBACK_URL,
  
  /**
   * Application version (for debugging/bug reports)
   * Optional but recommended
   */
  appVersion: import.meta.env.VITE_APP_VERSION,
  
  /**
   * Environment mode (development, production, etc.)
   */
  env: import.meta.env.MODE || 'development',
  
  /**
   * Check if running in production
   */
  isProduction: import.meta.env.PROD,
  
  /**
   * Check if running in development
   */
  isDevelopment: import.meta.env.DEV,
} as const

export type Config = typeof config
